import { ComponentConfig, componentConfigSchema } from 'common/stack/ComponentConfigDocuments';
import { AreIdentical } from 'commonUtil';
import { z } from 'zod';

//
// Component structure containing config plus values
//

export type ParameterValue = { identifier: string; value: string | number | boolean };

export const parameterValueSchema: z.ZodType<ParameterValue> = z.strictObject({
  identifier: z.string().min(1),
  value: z.string().min(0).or(z.number().int()).or(z.boolean()),
});

export type ParameterGroupValues = {
  identifier: string;
  enabled: boolean;
  parameters: ParameterValue[];
};

export const parameterGroupValuesSchema: z.ZodType<ParameterGroupValues> = z.strictObject({
  identifier: z.string().min(1),
  enabled: z.boolean(),
  parameters: z.array(parameterValueSchema),
});

export type EnvironmentVariableValue = {
  identifier: string;
  value: string;
};

export const environmentVariableValueSchema: z.ZodType<EnvironmentVariableValue> = z.strictObject({
  identifier: z.string().min(1),
  value: z.string(),
});

export type HostValue = {
  identifier: string;
  defaultDomainID: number | null;
  defaultDomain: string | null;
  tweakedDomainID: number | null;
  tweakedDomain: string | null;
  customDomain: string | null;
};

const hostValueSchema: z.ZodType<HostValue> = z.strictObject({
  identifier: z.string().min(1),
  defaultDomainID: z.number().int().min(1).nullable(),
  defaultDomain: z.string().nullable(),
  tweakedDomainID: z.number().int().min(1).nullable(),
  tweakedDomain: z.string().nullable(),
  customDomain: z.string().nullable(),
});

// TODO: Would be nice with one type for success and one for failure
export type ComponentWithValues = ComponentWithValuesSuccess | ComponentWithValuesError;

export type ComponentWithValuesSuccess = {
  stackComponentID: number;
  identifier: string;
  name: string;
  url: string;
  ref: string;
  sha: string;
  componentVersionID: number;
  config: ComponentConfig;
  errorCode: null;
  errorDetails: null;
  packageUrl: string;
  parameterGroups: ParameterGroupValues[];
  environmentVariables: EnvironmentVariableValue[];
  hosts: HostValue[];
};

export type ComponentWithValuesError = {
  stackComponentID: number;
  identifier: string;
  name: string;
  url: string | null;
  ref: string | null;
  sha: string | null;
  componentVersionID: number | null;
  config: ComponentConfig | null;
  errorCode: string;
  errorDetails: string;
  packageUrl: string | null;
  parameterGroups: ParameterGroupValues[];
  environmentVariables: EnvironmentVariableValue[];
  hosts: HostValue[];
};

export const componentWithValuesSchema = z.union([
  z.strictObject({
    // Success
    stackComponentID: z.number().int(),
    identifier: z.string().min(1),
    name: z.string().min(1),
    url: z.string(),
    ref: z.string(),
    sha: z.string(),
    componentVersionID: z.number(),
    config: componentConfigSchema,
    errorCode: z.null(),
    errorDetails: z.null(),
    packageUrl: z.string(),
    parameterGroups: z.array(parameterGroupValuesSchema),
    environmentVariables: z.array(environmentVariableValueSchema),
    hosts: z.array(hostValueSchema),
  }),
  z.strictObject({
    // Error
    stackComponentID: z.number().int(),
    identifier: z.string().min(1),
    name: z.string().min(1),
    url: z.string().nullable(),
    ref: z.string().nullable(),
    sha: z.string().nullable(),
    componentVersionID: z.number().nullable(),
    config: componentConfigSchema.nullable(),
    errorCode: z.string(),
    errorDetails: z.string(),
    packageUrl: z.string().nullable(),
    parameterGroups: z.array(parameterGroupValuesSchema),
    environmentVariables: z.array(environmentVariableValueSchema),
    hosts: z.array(hostValueSchema),
  }),
]);
export const _testComponentWithValues: AreIdentical<ComponentWithValues, z.infer<typeof componentWithValuesSchema>> = true;

//
// Stack Component, as queried from database
// TODO: Delete or rename
//

export type DbComponent = DbComponentSuccess | DbComponentError;

export type DbComponentSuccess = {
  stackComponentID: number;
  identifier: string;
  name: string;
  url: string;
  ref: string;
  sha: string;
  packageUrl: string;
  componentVersionID: number;
  config: ComponentConfig;
  sort: number;
  errorCode: null;
  errorDetails: null;
};

export type DbComponentError = {
  stackComponentID: number;
  identifier: string;
  name: string;
  url: string | null;
  ref: string | null;
  sha: string | null;
  packageUrl: string | null;
  componentVersionID: number | null;
  config: ComponentConfig | null;
  sort: number;
  errorCode: string;
  errorDetails: string;
};
